import type {
  VisitorConfig,
  Lead,
  Supplier,
  SupplierOffer,
  AppNotification,
  AccountingEntry,
  SitePage,
  SitePageSummary,
  BlogPost,
  BlogPostSummary,
  EnglishTranslations,
} from '../types';

// Client HTTP de l'application.
//
// `credentials: 'same-origin'` est indispensable : le cookie de session est
// httpOnly, il n'est donc pas lisible en JavaScript et doit être transmis par le
// navigateur lui-même.

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers:
        init.body instanceof FormData
          ? init.headers
          : { 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
  } catch {
    // Coupure réseau : distinguer ce cas d'une erreur serveur permet d'afficher
    // un message utile plutôt qu'un « erreur inconnue ».
    throw new ApiError('Connexion au serveur impossible. Vérifiez votre réseau.', 0);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) throw new ApiError(`Erreur serveur (${response.status}).`, response.status);
    throw new ApiError('Réponse inattendue du serveur.', response.status);
  }

  const payload = await response.json();
  if (!response.ok) {
    throw new ApiError(payload?.error || `Erreur serveur (${response.status}).`, response.status);
  }
  return payload as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

export interface CurrentUser {
  id: string;
  email: string | null;
  role: 'admin' | 'client' | 'supplier';
  displayName: string;
  leadId: string | null;
  supplierId: string | null;
  /**
   * Exploitant de la plateforme, distinct de l'administrateur d'une agence.
   * Ne sert qu'à afficher l'entrée de menu : l'autorisation réelle est vérifiée
   * côté serveur, qui répond 404 à qui n'a pas ce droit.
   */
  isPlatformAdmin?: boolean;
  /**
   * Prise en main de compte en cours : identifiant de l'exploitant qui dépanne.
   * Sa présence n'ouvre aucun droit ; elle fait afficher un bandeau permanent.
   */
  impersonatedBy?: string | null;
}

export interface BootstrapPayload {
  user: CurrentUser;
  config: VisitorConfig | null;
  leads: Lead[];
  suppliers: Supplier[];
  supplierOffers: SupplierOffer[];
  notifications: AppNotification[];
  accountingEntries: AccountingEntry[];
}

export type AdminNotificationType =
  | 'new_lead'
  | 'supplier_offer_new'
  | 'supplier_offer_updated'
  | 'supplier_invoice_submitted'
  | 'message_received'
  | 'installment_overdue'
  | 'system';

export interface NotificationEmailSettings {
  types: AdminNotificationType[];
  emails: string[];
  /** Ne contient que les refus explicites : l'absence d'un type vaut acceptation. */
  prefs: Partial<Record<AdminNotificationType, boolean>>;
}

export const api = {
  // Authentification
  login: (email: string, password: string) => post<{ ok: true }>('/api/auth/login', { email, password }),
  logout: () => post<{ ok: true }>('/api/auth/logout'),
  /** Met fin à une prise en main de compte. La session de l'exploitant reste ouverte ailleurs. */
  stopImpersonation: () => post<{ ok: true }>('/api/auth/stop-impersonation'),
  me: () => get<{ user: CurrentUser }>('/api/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true }>('/api/auth/change-password', { currentPassword, newPassword }),
  bootstrap: () => get<BootstrapPayload>('/api/bootstrap'),

  // Site vitrine — accessible sans session
  publicConfig: () =>
    get<{
      config: VisitorConfig | null;
      /** Vérification anti-robot de l'agence. La clé publique est lue par le widget. */
      captcha?: { enabled: boolean; siteKey: string };
      homePage: SitePage | null;
      menuPages: SitePageSummary[];
      /** Domaine principal de l'agence (personnalisé, ou sous-domaine feed-it.fr à défaut). */
      siteUrl: string;
    }>('/api/config'),
  saveConfig: (config: VisitorConfig) => put('/api/config', { config }),

  // Éditeur de pages
  getPageBySlug: (slug: string) => get<{ page: SitePage }>(`/api/pages/${slug}`),
  listPagesAdmin: () => get<{ pages: SitePage[] }>('/api/pages/admin'),
  createPage: (fields: { title: string; slug: string }) =>
    post<{ page: SitePage }>('/api/pages', fields),
  savePage: (
    id: string,
    fields: Partial<Pick<SitePage, 'title' | 'slug' | 'inMenu' | 'menuOrder' | 'sections'>>
  ) => patch<{ page: SitePage }>(`/api/pages/${id}`, fields),
  deletePage: (id: string) => del<{ ok: true }>(`/api/pages/${id}`),
  uploadPageMedia: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ url: string }>('/api/pages/media', { method: 'POST', body: form });
  },

  // Blog
  listBlogPosts: () => get<{ posts: BlogPostSummary[] }>('/api/blog-posts'),
  getBlogPostBySlug: (slug: string) => get<{ post: BlogPost }>(`/api/blog-posts/${slug}`),
  listBlogPostsAdmin: () => get<{ posts: BlogPostSummary[] }>('/api/blog-posts/admin/list'),
  getBlogPostAdmin: (id: string) => get<{ post: BlogPost }>(`/api/blog-posts/admin/${id}`),
  createBlogPost: (fields: { title: string; slug: string }) =>
    post<{ post: BlogPost }>('/api/blog-posts', fields),
  saveBlogPost: (
    id: string,
    fields: Partial<
      Pick<
        BlogPost,
        'title' | 'slug' | 'excerpt' | 'coverImageUrl' | 'metaDescription' | 'authorName' | 'status' | 'sections'
      >
    >
  ) => patch<{ post: BlogPost }>(`/api/blog-posts/${id}`, fields),
  deleteBlogPost: (id: string) => del<{ ok: true }>(`/api/blog-posts/${id}`),

  // Dossiers
  submitQuote: (payload: {
    clientName: string;
    clientEmail: string;
    clientPhone: string;
    eventDate: string;
    guestsCount: number;
    eventType: string;
    customAnswers: Record<string, string>;
    /** Jeton hCaptcha, vérifié côté serveur avant tout enregistrement. */
    captchaToken?: string | null;
  }) => post<{ lead: Lead }>('/api/leads', payload),
  /** Création manuelle depuis le CRM (prospect contacté par téléphone ou en personne). */
  createLead: (payload: {
    clientName: string;
    clientEmail: string;
    clientPhone?: string;
    eventDate?: string;
    guestsCount?: number;
    eventType?: string;
  }) => post<{ lead: Lead }>('/api/leads/admin', payload),
  updateLead: (id: string, fields: Partial<Lead>) => patch<{ lead: Lead }>(`/api/leads/${id}`, fields),

  // Fournisseurs
  createSupplier: (supplier: Omit<Supplier, 'id'>) =>
    post<{ supplier: Supplier }>('/api/suppliers', supplier),
  updateSupplier: (id: string, fields: Partial<Supplier>) =>
    patch<{ supplier: Supplier }>(`/api/suppliers/${id}`, fields),
  /** Candidature spontanée, sans session — accessible depuis le site public. */
  applyAsSupplier: (payload: {
    name: string;
    email: string;
    phone: string;
    specialty: string;
    city?: string;
    availability?: string;
    hasDrivingLicense?: boolean;
    experience?: string;
    desiredHourlyRate?: string;
  }) => post<{ supplier: Supplier }>('/api/suppliers/apply', payload),

  // Offres
  createOffer: (offer: Omit<SupplierOffer, 'id'>) =>
    post<{ offer: SupplierOffer }>('/api/offers', offer),
  createOffersBulk: (payload: {
    leadId: string;
    supplierIds: string[];
    amount: number;
    description: string;
  }) => post<{ offers: SupplierOffer[] }>('/api/offers/bulk', payload),
  updateOffer: (id: string, fields: Partial<SupplierOffer>) =>
    patch<{ offer: SupplierOffer }>(`/api/offers/${id}`, fields),

  // Notifications
  markNotificationRead: (id: string) => patch<{ ok: true }>(`/api/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => post<{ ok: true }>('/api/notifications/read-all'),
  clearNotifications: () => del<{ ok: true }>('/api/notifications'),
  getNotificationEmailSettings: () =>
    get<NotificationEmailSettings>('/api/notifications/email-settings'),
  updateNotificationEmailSettings: (payload: { emails: string[]; prefs: Record<string, boolean> }) =>
    patch<{ emails: string[]; prefs: Record<string, boolean> }>(
      '/api/notifications/email-settings',
      payload
    ),

  // Comptabilité
  createAccountingEntry: (entry: Omit<AccountingEntry, 'id' | 'createdAt'>) =>
    post<{ entry: AccountingEntry }>('/api/accounting', entry),
  deleteAccountingEntry: (id: string) => del<{ ok: true }>(`/api/accounting/${id}`),

  /**
   * Lecture automatique d'une facture. Le résultat est une PROPOSITION :
   * rien n'entre au grand livre sans relecture et validation.
   */
  scanInvoice: (file: File) => {
    const form = new FormData();
    form.append('document', file);
    return request<OcrScanResult>('/api/ocr/scan', { method: 'POST', body: form });
  },

  // Règlements
  paymentStatus: () => get<StripeStatus>('/api/payments/status'),
  /** Ouvre une session Stripe Checkout et rend l'URL de redirection. */
  startCheckout: (leadId: string, kind: 'deposit' | 'balance' | 'full') =>
    post<{ url: string; sessionId: string; amountCents: number }>('/api/payments/checkout', {
      leadId,
      kind,
    }),
  /** Règlement d'une échéance précise : le montant vient de l'échéance, pas du solde. */
  startInstallmentCheckout: (installmentId: string) =>
    post<{ url: string; sessionId: string; amountCents: number }>('/api/payments/checkout', {
      installmentId,
    }),
  leadPayments: (leadId: string) => get<{ payments: PaymentRecord[] }>(`/api/payments/lead/${leadId}`),
  refundPayment: (id: string) => post<{ ok: true }>(`/api/payments/${id}/refund`),

  // Échéanciers de règlement
  leadInstallments: (leadId: string) =>
    get<{ installments: Installment[]; totals: ScheduleTotalsPayload; dueCents: number }>(
      `/api/installments/lead/${leadId}`
    ),
  /**
   * Crée ou remplace l'échéancier d'un dossier.
   * Le montant à répartir n'est jamais transmis : le serveur le déduit du devis
   * retenu, diminué de ce qui est déjà encaissé.
   */
  createSchedule: (
    leadId: string,
    payload:
      | {
          mode: 'frequency';
          count: number;
          firstDueDate: string;
          interval: 'week' | 'month';
          intervalCount?: number;
          depositPercent?: number;
        }
      | { mode: 'dates'; entries: Array<{ label?: string; amountCents: number; dueDate: string }> }
  ) => post<{ installments: Installment[]; totals: ScheduleTotalsPayload }>(
    `/api/installments/lead/${leadId}`,
    payload
  ),
  pendingInstallments: () =>
    get<{
      installments: PendingInstallment[];
      overdueCount: number;
      pendingCents: number;
      overdueCents: number;
    }>('/api/installments/pending'),
  settleInstallment: (
    id: string,
    payload: { method: string; settledOn?: string; note?: string }
  ) => post<{ ok: true }>(`/api/installments/${id}/settle`, payload),
  cancelInstallment: (id: string) => post<{ ok: true }>(`/api/installments/${id}/cancel`),
  remindInstallment: (id: string) =>
    post<{ ok: true; reminderCount: number }>(`/api/installments/${id}/remind`),

  // Messagerie interne des dossiers
  leadThreads: (leadId: string) => get<{ leadId: string; threads: MessageThread[] }>(`/api/messages/${leadId}`),
  sendMessage: (leadId: string, payload: { channelKind: 'client' | 'supplier'; supplierId?: string | null; body: string }) =>
    post<{ message: LeadMessage }>(`/api/messages/${leadId}`, payload),
  markThreadRead: (leadId: string, payload: { channelKind: 'client' | 'supplier'; supplierId?: string | null }) =>
    post<{ ok: true }>(`/api/messages/${leadId}/read`, payload),
  unreadMessages: () =>
    get<{ unread: Array<{ leadId: string; count: number }>; total: number }>('/api/messages'),

  /**
   * Relecture du site vitrine.
   * Rend des constats, ne modifie rien : c'est l'agence qui corrige.
   */
  reviewSite: () =>
    post<{
      findings: Array<{ severity: 'high' | 'medium' | 'low'; area: string; message: string }>;
      aiUsed: boolean;
    }>('/api/ai/review-site'),

  /**
   * Traduction anglaise du site vitrine par IA. Remplace entièrement
   * `config.translations.en` côté serveur ; l'agence peut ensuite corriger
   * le résultat champ par champ (voir TranslationPanel).
   */
  translateSite: () =>
    post<{ translations: EnglishTranslations }>('/api/ai/translate-site'),

  // Facturation client
  invoices: (status?: string) =>
    get<{
      invoices: ClientInvoice[];
      totals: {
        invoicedCents: number;
        paidCents: number;
        outstandingCents: number;
        creditedCents: number;
      };
    }>(`/api/invoices${status ? `?status=${status}` : ''}`),
  invoice: (id: string) => get<{ invoice: ClientInvoice }>(`/api/invoices/${id}`),
  leadInvoices: (leadId: string) =>
    get<{ invoices: ClientInvoice[] }>(`/api/invoices/lead/${leadId}`),
  /** Crée un brouillon. Sans lignes fournies, le devis retenu est repris. */
  createInvoice: (leadId: string, payload: { lines?: ClientInvoiceLine[]; notes?: string } = {}) =>
    post<{ invoice: ClientInvoice }>(`/api/invoices/lead/${leadId}`, payload),
  updateInvoice: (
    id: string,
    payload: Partial<{ lines: ClientInvoiceLine[]; notes: string; dueOn: string; clientAddress: string; clientVatNumber: string }>
  ) => patch<{ invoice: ClientInvoice }>(`/api/invoices/${id}`, payload),
  deleteInvoice: (id: string) => del<{ ok: true }>(`/api/invoices/${id}`),
  /** Attribue le numéro et fige la pièce. Irréversible : elle s'annule ensuite par un avoir. */
  issueInvoice: (id: string) => post<{ invoice: ClientInvoice }>(`/api/invoices/${id}/issue`),
  payInvoice: (id: string, paidOn?: string) =>
    post<{ ok: true }>(`/api/invoices/${id}/pay`, { paidOn }),
  creditInvoice: (id: string, reason?: string) =>
    post<{ creditNote: ClientInvoice }>(`/api/invoices/${id}/credit`, { reason }),

  /** Agrégats de l'agence : sur tous les dossiers, pas sur la page chargée. */
  agencyStats: () => get<AgencyStats>('/api/stats'),

  // Campagnes e-mail
  campaigns: () => get<{ campaigns: Campaign[] }>('/api/campaigns'),
  /** Compte les destinataires sans rien écrire ni envoyer. */
  previewCampaign: (audience: Partial<CampaignAudience>) =>
    post<{
      audience: CampaignAudience;
      recipientCount: number;
      optedOutCount: number;
      sample: Array<{ clientName: string; clientEmail: string }>;
    }>('/api/campaigns/preview', { audience }),
  sendCampaign: (payload: { subject: string; body: string; audience: Partial<CampaignAudience> }) =>
    post<{ id: string; sent: number; failed: number }>('/api/campaigns', payload),
  /** Reprend les envois en échec. Les réussis ne sont jamais rejoués. */
  retryCampaign: (id: string) => post<{ sent: number; failed: number }>(`/api/campaigns/${id}/retry`),
  campaignRecipients: (id: string) =>
    get<{ recipients: Array<{ email: string; status: string; error: string; sentAt: string | null }> }>(
      `/api/campaigns/${id}/recipients`
    ),

  // Ressources de production
  resources: () => get<{ resources: Resource[] }>('/api/resources'),
  createResource: (payload: Partial<Resource> & { name: string; quantity: number }) =>
    post<{ resource: Resource }>('/api/resources', payload),
  updateResource: (id: string, payload: Partial<Resource>) =>
    patch<{ resource: Resource }>(`/api/resources/${id}`, payload),
  deleteResource: (id: string) => del<{ ok: true }>(`/api/resources/${id}`),
  resourceAssignments: (params: { leadId?: string; from?: string; to?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.leadId) search.set('leadId', params.leadId);
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    const suffix = search.toString();
    return get<{ assignments: ResourceAssignment[] }>(
      `/api/resources/assignments${suffix ? `?${suffix}` : ''}`
    );
  },
  /** Rejette un 409 dont le corps porte le détail du conflit. */
  assignResource: (
    resourceId: string,
    payload: { leadId: string; quantity?: number; startsOn: string; endsOn: string; notes?: string }
  ) => post<{ id: string }>(`/api/resources/${resourceId}/assign`, payload),
  unassignResource: (assignmentId: string) =>
    del<{ ok: true }>(`/api/resources/assignments/${assignmentId}`),
  resourceAvailability: (from: string, to: string) =>
    get<{ from: string; to: string; availability: ResourceAvailability[] }>(
      `/api/resources/availability?from=${from}&to=${to}`
    ),

  // Signature et documents contractuels
  /**
   * Signature du devis par le client.
   * Le montant n'est pas transmis : il est relu en base au moment de signer.
   */
  signQuote: (
    leadId: string,
    payload: { signerName: string; accepted: boolean; signatureImage?: string | null }
  ) => post<{ signature: SignatureRecord }>(`/api/documents/quotes/${leadId}/sign`, payload),
  leadSignatures: (leadId: string) =>
    get<{ signatures: SignatureRecord[] }>(`/api/documents/signatures/${leadId}`),
  /** `matchesCurrent: false` signale un devis renégocié depuis sa signature. */
  verifyQuoteSignature: (leadId: string) =>
    get<{ signed: boolean; signature?: SignatureRecord; matchesCurrent?: boolean }>(
      `/api/documents/signatures/${leadId}/verify`
    ),

  documentTemplates: () => get<{ templates: DocumentTemplate[] }>('/api/documents/templates'),
  createDocumentTemplate: (payload: {
    title: string;
    body?: string;
    requiresSignature?: boolean;
    fields?: DocumentField[];
  }) => post<{ id: string }>('/api/documents/templates', payload),
  updateDocumentTemplate: (
    id: string,
    payload: Partial<{ title: string; body: string; requiresSignature: boolean; isActive: boolean; fields: DocumentField[] }>
  ) => patch<{ ok: true }>(`/api/documents/templates/${id}`, payload),

  leadDocuments: (leadId: string) =>
    get<{ requests: DocumentRequest[] }>(`/api/documents/requests/${leadId}`),
  requestDocument: (leadId: string, documentId: string) =>
    post<{ id: string }>(`/api/documents/requests/${leadId}`, { documentId }),
  completeDocument: (
    id: string,
    payload: { answers?: Record<string, string>; signerName?: string; signatureImage?: string | null }
  ) => post<{ ok: true }>(`/api/documents/requests/${id}/complete`, payload),
  cancelDocument: (id: string) => post<{ ok: true }>(`/api/documents/requests/${id}/cancel`),

  // Modération du mur convives
  pendingGuestPosts: () => get<{ posts: PendingGuestPost[] }>('/api/documents/moderation'),
  moderateGuestPost: (postId: string, payload: { decision: 'approve' | 'reject'; reason?: string }) =>
    post<{ ok: true; decision: string }>(`/api/documents/moderation/${postId}`, payload),

  // Rapprochement bancaire
  bankAccounts: () => get<{ accounts: BankAccount[] }>('/api/bank/accounts'),
  createBankAccount: (payload: { label: string; iban?: string }) =>
    post<{ account: BankAccount }>('/api/bank/accounts', payload),
  updateBankAccount: (id: string, payload: { label?: string; isArchived?: boolean }) =>
    patch<{ ok: true }>(`/api/bank/accounts/${id}`, payload),
  /** Découpe le fichier et rend les premières lignes, sans rien écrire. */
  previewStatement: (content: string, delimiter?: string) =>
    post<{ delimiter: string; totalRows: number; columns: number; sample: string[][] }>(
      '/api/bank/preview',
      { content, delimiter }
    ),
  importStatement: (payload: {
    bankAccountId: string;
    content: string;
    fileName?: string;
    delimiter?: string;
    mapping: BankColumnMapping;
  }) =>
    post<{
      importId: string;
      imported: number;
      duplicates: number;
      rejected: number;
      rejectedRows: Array<{ line: number; reason: string }>;
    }>('/api/bank/import', payload),
  bankImports: () => get<{ imports: BankImportRecord[] }>('/api/bank/imports'),
  bankTransactions: (params: { status?: string; accountId?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.accountId) search.set('accountId', params.accountId);
    const suffix = search.toString();
    return get<{
      transactions: BankTransaction[];
      status: {
        unmatchedCount: number;
        matchedCount: number;
        ignoredCount: number;
        matchedTotalCents: number;
      };
    }>(`/api/bank/transactions${suffix ? `?${suffix}` : ''}`);
  },
  bankSuggestions: (id: string) =>
    get<{ suggestions: BankMatchSuggestion[] }>(`/api/bank/transactions/${id}/suggestions`),
  matchBankTransaction: (id: string, payload: { entryId?: string; paymentId?: string }) =>
    post<{ ok: true }>(`/api/bank/transactions/${id}/match`, payload),
  unmatchBankTransaction: (id: string) => post<{ ok: true }>(`/api/bank/transactions/${id}/unmatch`),
  ignoreBankTransaction: (id: string) => post<{ ok: true }>(`/api/bank/transactions/${id}/ignore`),

  // Comptes et accès
  listUsers: () => get<{ users: ManagedUser[] }>('/api/users'),
  createUser: (payload: { email: string; role: 'admin' | 'client' | 'supplier'; scopeId?: string }) =>
    post<{ user: { id: string; email: string; role: string }; temporaryPassword: string | null }>(
      '/api/users',
      payload
    ),
  resetUserPassword: (id: string) =>
    post<{ email: string; temporaryPassword: string }>(`/api/users/${id}/reset-password`),
  setUserActive: (id: string, isActive: boolean) =>
    patch<{ ok: true }>(`/api/users/${id}`, { isActive }),
};

export interface OcrExtraction {
  entityName: string;
  siret: string;
  tvaNumber: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  description: string;
  amountHT: number;
  tvaRate: number;
  amountTVA: number;
  amountTTC: number;
  suggestedChargeCode: string;
  suggestedChargeLabel: string;
  suggestedJournal: 'AC' | 'VT';
  /** Estimation de fiabilité par le modèle, pas une mesure de précision OCR. */
  confidence: number;
  /** Champs illisibles, totaux incohérents, mentions légales manquantes. */
  warnings: string[];
}

export interface OcrScanResult {
  extraction: OcrExtraction;
  document: { fileName: string; fileUrl: string };
}

export interface StripeStatus {
  configured: boolean;
  webhookConfigured: boolean;
  /** true dès que la clé est en mode « live » : les paiements encaissent réellement. */
  liveMode: boolean;
  currency: string;
  depositPercent: number;
  /** Variables d'environnement manquantes, pour un diagnostic immédiat. */
  missing: string[];
}

export interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  kind: 'deposit' | 'balance' | 'full';
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled' | 'refunded';
  paidAt: string | null;
  failureReason: string | null;
  createdAt: string;
}

/**
 * Échéance d'un échéancier de règlement.
 *
 * `state` est calculé par le serveur à partir de la date du jour : il n'existe
 * pas en base, où seuls « en attente / réglée / annulée » sont enregistrés.
 */
export interface Installment {
  id: string;
  leadId: string;
  sequence: number;
  label: string;
  amountCents: number;
  amount: number;
  dueDate: string;
  status: 'pending' | 'paid' | 'canceled';
  state: 'paid' | 'overdue' | 'due_soon' | 'pending' | 'canceled';
  method: string | null;
  settledAt: string | null;
  settledNote: string;
  reminderCount: number;
  lastReminderAt: string | null;
}

/** Échéance telle que la présente la file d'attente de l'agence, dossier compris. */
export interface PendingInstallment extends Installment {
  clientName: string;
  clientEmail: string;
  eventType: string;
  eventDate: string;
}

export interface ScheduleTotalsPayload {
  totalCents: number;
  paidCents: number;
  pendingCents: number;
  overdueCents: number;
  nextSequence: number | null;
}

// --- Facturation client ---

/**
 * Ligne de facture CLIENT, distincte de `InvoiceLine` qui sert à la facturation
 * des agences par la plateforme. Les deux coexistent : l'une porte un taux de
 * TVA par ligne — une prestation mêle 10 % et 20 % — l'autre un taux unique.
 */
export interface ClientInvoiceLine {
  id?: string;
  label: string;
  quantity: number;
  unitPriceCents: number;
  vatRate: number;
}

export interface ClientInvoice {
  id: string;
  leadId: string | null;
  kind: 'invoice' | 'credit_note';
  parentInvoiceId: string | null;
  /** Nul tant que la pièce est un brouillon : le numéro s'attribue à l'émission. */
  number: string | null;
  status: 'draft' | 'issued' | 'paid' | 'cancelled';
  issuedOn: string | null;
  dueOn: string | null;
  paidOn: string | null;
  clientName: string;
  clientEmail: string;
  clientAddress: string;
  clientVatNumber: string;
  issuerName: string;
  issuerAddress: string;
  issuerSiret: string;
  issuerVatNumber: string;
  amountHtCents: number;
  amountVatCents: number;
  amountTtcCents: number;
  paymentTerms: string;
  latePenaltyTerms: string;
  notes: string;
  createdAt: string;
  lines: ClientInvoiceLine[];
}

// --- Chiffres agrégés ---

/**
 * Agrégats de l'agence, calculés côté serveur sur TOUS les dossiers.
 *
 * Ils l'étaient auparavant dans le navigateur, sur la liste déjà chargée — donc
 * plafonnée. Passé cinquante dossiers, le chiffre d'affaires affiché n'était
 * plus celui de l'agence mais celui de sa première page.
 */
export interface AgencyStats {
  activeCount: number;
  turnoverHT: number;
  turnoverTTC: number;
  paidTTC: number;
  supplierCostsHT: number;
  marginHT: number;
  marginRate: number;
  byStatus: Record<string, number>;
  conversion: { proposed: number; accepted: number; rate: number };
  upcoming: { events: number; guests: number };
  trend: Array<{ month: string; leads: number; signed: number }>;
}

// --- Campagnes e-mail ---

export interface CampaignAudience {
  statuses: string[];
  eventFrom: string | null;
  eventTo: string | null;
}

export interface Campaign {
  id: string;
  subject: string;
  body: string;
  audience: CampaignAudience;
  status: 'draft' | 'sending' | 'sent' | 'canceled';
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  createdAt: string;
  sentAt: string | null;
}

// --- Ressources de production ---

export interface Resource {
  id: string;
  category: string;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  /** Jours d'immobilisation avant la prestation (chargement, préparation). */
  prepBufferDays: number;
  /** Jours d'immobilisation après (retour, nettoyage). */
  returnBufferDays: number;
  isActive: boolean;
}

export interface ResourceAssignment {
  id: string;
  resourceId: string;
  resourceName: string;
  category: string;
  unit: string;
  leadId: string;
  clientName: string;
  eventType: string;
  quantity: number;
  startsOn: string;
  endsOn: string;
  /** Période réellement immobilisée, tampons compris. */
  blockedFrom: string;
  blockedTo: string;
  notes: string;
}

export interface ResourceAvailability {
  resource: Resource;
  /** Pire disponibilité sur la période : c'est elle qui décide. */
  minFree: number;
  days: Array<{ date: string; used: number; free: number; assignmentIds: string[] }>;
}

export interface ResourceConflict {
  conflicting: Array<{ id: string; quantity: number; startsOn: string; endsOn: string }>;
  peakUsed: number;
  available: number;
  requested: number;
}

// --- Signature et documents contractuels ---

export interface SignatureRecord {
  id: string;
  subjectKind: 'quote' | 'document' | 'image_rights';
  subjectId: string;
  leadId: string | null;
  signerName: string;
  signerEmail: string;
  hasImage: boolean;
  /** Empreinte de ce qui a été signé : c'est elle qui donne sa valeur à la pièce. */
  contentHash: string;
  contentSummary: string;
  signedAt: string;
}

export interface DocumentField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'number' | 'checkbox';
  required: boolean;
}

export interface DocumentTemplate {
  id: string;
  title: string;
  body: string;
  requiresSignature: boolean;
  fields: DocumentField[];
  isActive: boolean;
}

export interface DocumentRequest {
  id: string;
  title: string;
  body: string;
  fields: DocumentField[];
  requiresSignature: boolean;
  status: 'pending' | 'completed' | 'canceled';
  answers: Record<string, string>;
  signerName: string | null;
  signedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface PendingGuestPost {
  id: string;
  leadId: string;
  mediaType: 'image' | 'video';
  url: string;
  caption: string;
  createdAt: string;
  authorName: string;
  authorEmail: string;
  /** Le convive a-t-il déclaré avoir l'accord des personnes photographiées ? */
  authorConsented: boolean;
  clientName: string;
  eventType: string;
}

// --- Rapprochement bancaire ---

export interface BankAccount {
  id: string;
  label: string;
  ibanLast4: string;
  currency?: string;
  isArchived?: boolean;
  unmatchedCount?: number;
  lastBookedOn?: string;
}

export interface BankTransaction {
  id: string;
  bankAccountId: string;
  bookedOn: string;
  label: string;
  /** Signé : négatif au débit, positif au crédit. */
  amountCents: number;
  status: 'unmatched' | 'matched' | 'ignored';
  matchedEntryId: string | null;
  matchedPaymentId: string | null;
  matchedAt: string | null;
  matchedLabel: string | null;
}

export interface BankMatchSuggestion {
  id: string;
  kind: 'entry' | 'payment';
  amountCents: number;
  date: string;
  label: string;
  /** 0 à 1. Au-delà de 0,8, le rapprochement est proposé d'emblée. */
  confidence: number;
}

export interface BankColumnMapping {
  date: number;
  label: number;
  amount?: number;
  debit?: number;
  credit?: number;
  headerRows?: number;
}

export interface BankImportRecord {
  id: string;
  fileName: string;
  accountLabel: string;
  rowsTotal: number;
  rowsImported: number;
  rowsDuplicate: number;
  rowsRejected: number;
  createdAt: string;
}

export interface LeadMessage {
  id: string;
  channelKey: string;
  authorUserId: string | null;
  authorRole: 'admin' | 'client' | 'supplier';
  authorName: string;
  body: string;
  createdAt: string;
}

/**
 * Fil de discussion d'un dossier. Un fil par interlocuteur : le client ne voit
 * pas les échanges avec les partenaires, et réciproquement.
 */
export interface MessageThread {
  key: string;
  kind: 'client' | 'supplier';
  supplierId: string | null;
  label: string;
  unreadCount: number;
  messages: LeadMessage[];
}

export interface ManagedUser {
  id: string;
  email: string;
  role: 'admin' | 'client' | 'supplier';
  displayName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  scopeId: string | null;
  scopeLabel: string | null;
}

function put<T>(path: string, body: unknown) {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

// ---------------------------------------------------------------------------
// Administration de la plateforme
// ---------------------------------------------------------------------------

export type OrganizationStatus = 'active' | 'suspended' | 'closed';
export type OrganizationPlan = 'trial' | 'standard' | 'premium';
/** Étape de la relation commerciale. Sans effet technique sur le service rendu. */
export type CommercialStage = 'prospect' | 'trial' | 'customer' | 'at_risk' | 'churned';

/** Champs partagés par la liste et la fiche : tout ce qui décrit l'agence elle-même. */
export interface PlatformOrganizationBase {
  id: string;
  slug: string;
  name: string;
  status: OrganizationStatus;
  plan: OrganizationPlan;
  commercialStage: CommercialStage;
  trialEndsAt: string | null;
  monthlyFeeCents: number;
  legalName: string | null;
  legalEmail: string | null;
  legalAddress: string | null;
  /** Facturation de l'agence par la plateforme, distincte du contact RGPD. */
  billingEmail: string | null;
  siret: string | null;
  vatNumber: string | null;
  /** Identité reprise dans les e-mails : signature, pied de page, accusé de réception. */
  brandName: string | null;
  contactPhone: string | null;
  /** Boîte interne, destinataire des alertes « nouveau prospect ». */
  alertEmail: string | null;
  depositPercent: number;
  currency: string;
  sitePublic: boolean;
  mailFrom: string | null;
  mailReplyTo: string | null;
  createdAt: string;
}

export interface PlatformOrganization extends PlatformOrganizationBase {
  hostnames: string[];
  /** Noms des clés déposées, jamais leur valeur. Permet d'afficher l'état des services. */
  secretNames: string[];
  /** Volumétrie seulement : le contenu des dossiers reste inaccessible. */
  userCount: number;
  staffCount: number;
  leadCount: number;
  leadCountLast30Days: number;
  lastLeadAt: string | null;
  lastStaffLoginAt: string | null;
  collectedCents: number;
  lastInteractionAt: string | null;
  nextActionAt: string | null;
}

export interface PlatformSecret {
  name: string;
  /** Aperçu du type « sk_live_…4f2a ». La valeur n'est jamais renvoyée. */
  hint: string | null;
  updatedAt: string;
  /** Faux si le secret a été chiffré avec une autre clé maîtresse. */
  readable: boolean;
}

export interface TenantServicesStatus {
  stripe: {
    configured: boolean;
    webhookConfigured: boolean;
    liveMode: boolean;
    currency: string;
    depositPercent: number;
    missing: string[];
  };
  mail: { configured: boolean; from: string; missing: string[] };
  ai: { configured: boolean; missing: string[] };
}

/** Indicateurs du parc. Dénombrement et configuration, jamais de contenu métier. */
export interface PlatformMetrics {
  counts: {
    total: number;
    active: number;
    suspended: number;
    closed: number;
    createdLast30Days: number;
  };
  pipeline: { prospect: number; trial: number; customer: number; atRisk: number; churned: number };
  revenue: {
    /** Abonnements déclarés des agences en service. N'est pas un encaissement constaté. */
    mrrCents: number;
    arrCents: number;
    /** Réglages effectivement encaissés par Stripe, toutes agences confondues. */
    collectedCents: number;
    paymentsCount: number;
    paymentsFailed30d: number;
  };
  volumes: {
    accounts: number;
    staffAccounts: number;
    leads: number;
    leadsLast30Days: number;
    leadsOpen: number;
    suppliers: number;
    guestPosts: number;
  };
  /** Facturation des agences PAR la plateforme, distincte des encaissements Stripe des agences. */
  billing: {
    invoicedHtCents: number;
    paidTtcCents: number;
    dueTtcCents: number;
    overdueTtcCents: number;
    overdueCount: number;
    draftCount: number;
  };
  signupsLast30Days: Array<{ day: string; count: number }>;
  expiringTrials: Array<{ id: string; slug: string; name: string; trialEndsAt: string }>;
  followUps: Array<{
    id: string;
    organizationId: string;
    organizationName: string;
    slug: string;
    kind: InteractionKind;
    subject: string;
    nextActionAt: string;
  }>;
  alerts: PlatformAlert[];
  recentAudit: PlatformAuditEntry[];
}

export interface PlatformAlert {
  organizationId: string;
  organizationName: string;
  slug: string;
  /** `blocking` empêche l'agence de fonctionner, `warning` la dégrade. */
  severity: 'blocking' | 'warning';
  code: string;
  message: string;
}

export interface PlatformAuditEntry {
  id: string;
  actorEmail: string | null;
  organizationId: string | null;
  organizationSlug: string | null;
  action: string;
  details: Record<string, unknown>;
  ip: string | null;
  occurredAt: string;
}

export interface PlatformDomain {
  hostname: string;
  isPrimary: boolean;
  createdAt: string;
}

export interface OrganizationStats {
  accounts: number;
  staffAccounts: number;
  clientAccounts: number;
  supplierAccounts: number;
  leads: number;
  leadsOpen: number;
  leadsLast30Days: number;
  lastLeadAt: string | null;
  suppliers: number;
  offers: number;
  accountingEntries: number;
  notifications: number;
  guestPosts: number;
  payments: number;
  collectedCents: number;
  lastStaffLoginAt: string | null;
}

export interface RetentionRule {
  entity: string;
  months: number;
  legalBasis: string;
  updatedAt: string;
}

export interface RetentionRun {
  entity: string;
  deleted: number;
  ranAt: string;
}

/**
 * Demande d'exercice des droits. L'adresse de la personne concernée n'est jamais
 * renvoyée : la plateforme n'a pas à savoir qui exerce ses droits auprès d'une
 * agence, seulement qu'une demande attend une réponse.
 */
export interface DataRequestSummary {
  kind: 'access' | 'erasure' | 'rectification';
  status: 'pending' | 'completed' | 'refused';
  requestedAt: string;
  completedAt: string | null;
}

export interface OrganizationDetail {
  organization: PlatformOrganizationBase;
  domains: PlatformDomain[];
  stats: OrganizationStats;
  services: TenantServicesStatus;
  retention: RetentionRule[];
  retentionRuns: RetentionRun[];
  dataRequests: DataRequestSummary[];
  counts: { notes: number; interactions: number };
}

export interface SystemStatus {
  backup: {
    directory: string;
    exists: boolean;
    lastAt: string | null;
    ageHours: number | null;
    sizeBytes: number;
    count: number;
    stale: boolean;
    staleAfterHours: number;
  };
  media: { directory: string; sizeBytes: number; files: number };
  retention: { lastRunAt: string | null; stale: boolean };
}

export interface PlatformAdminAccount {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  organizationId: string;
  organizationName: string;
  slug: string;
  lastLoginAt: string | null;
  createdAt: string;
}

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'cancelled';

export interface PlatformInvoice {
  id: string;
  organizationId: string;
  organizationName: string | null;
  slug: string | null;
  kind: 'invoice' | 'credit_note';
  parentInvoiceId: string | null;
  /** Nul tant que la pièce est un brouillon : un numéro attribué puis abandonné trouerait la séquence. */
  number: string | null;
  status: InvoiceStatus;
  periodStart: string | null;
  periodEnd: string | null;
  issuedOn: string | null;
  dueOn: string | null;
  paidOn: string | null;
  billingName: string;
  billingEmail: string;
  billingAddress: string;
  vatNumber: string;
  amountHtCents: number;
  vatRate: number;
  amountVatCents: number;
  amountTtcCents: number;
  paymentNote: string;
  notes: string;
  reminderCount: number;
  remindedAt: string | null;
  createdAt: string;
}

export interface InvoiceLine {
  id?: string;
  label: string;
  quantity: number;
  unitPriceCents: number;
}

export interface BillingTotals {
  invoicedHtCents: number;
  paidTtcCents: number;
  dueTtcCents: number;
  overdueTtcCents: number;
  overdueCount: number;
  draftCount: number;
  collectionRate: number;
}

/** Comptes de l'équipe de l'agence. Les comptes clients ne sont jamais listés. */
export interface PlatformAccount {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  isPlatformAdmin: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PlatformNote {
  id: string;
  authorEmail: string | null;
  body: string;
  pinned: boolean;
  createdAt: string;
}

export type InteractionKind = 'call' | 'email' | 'meeting' | 'demo' | 'support' | 'other';

export interface PlatformInteraction {
  id: string;
  authorEmail: string | null;
  kind: InteractionKind;
  subject: string;
  body: string;
  occurredAt: string;
  /** Suite à donner. C'est elle qui distingue un CRM d'un bloc-notes. */
  nextActionAt: string | null;
  createdAt: string;
}

export interface DeletionBlocker {
  code: string;
  message: string;
}

export const platformApi = {
  listOrganizations: () =>
    get<{ organizations: PlatformOrganization[]; secretsConfigured: boolean }>(
      '/api/platform/organizations'
    ),
  createOrganization: (payload: {
    name: string;
    slug: string;
    hostname: string;
    adminEmail?: string;
    legalName?: string;
    plan?: OrganizationPlan;
    monthlyFeeCents?: number;
    trialDays?: number;
  }) =>
    post<{
      organization: { id: string; slug: string; name: string; hostname: string };
      adminEmail: string | null;
      temporaryPassword: string | null;
    }>('/api/platform/organizations', payload),
  updateOrganization: (id: string, fields: Partial<PlatformOrganizationBase>) =>
    patch<{ ok: true }>(`/api/platform/organizations/${id}`, fields),

  // Tableau de bord et journal
  metrics: () => get<PlatformMetrics>('/api/platform/metrics'),
  audit: (params: { organizationId?: string; action?: string; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.organizationId) search.set('organizationId', params.organizationId);
    if (params.action) search.set('action', params.action);
    if (params.limit) search.set('limit', String(params.limit));
    return get<{ entries: PlatformAuditEntry[] }>(`/api/platform/audit?${search}`);
  },

  /** Sauvegardes, encombrement des médias, purge automatique. */
  system: () => get<SystemStatus>('/api/platform/system'),
  /** Envoi immédiat du récapitulatif, sans décaler l'échéance hebdomadaire. */
  sendDigest: () => post<{ sent: boolean }>('/api/platform/system/digest'),
  listAdmins: () => get<{ admins: PlatformAdminAccount[] }>('/api/platform/admins'),

  // Fiche d'une agence
  organization: (id: string) => get<OrganizationDetail>(`/api/platform/organizations/${id}`),
  /** Interroge réellement Stripe : une clé déposée n'est pas une clé valide. */
  testStripe: (id: string) =>
    post<{ ok: boolean; liveMode: boolean; account?: string; detail: string }>(
      `/api/platform/organizations/${id}/test-stripe`
    ),

  addDomain: (id: string, hostname: string) =>
    post<{ ok: true; hostname: string }>(`/api/platform/organizations/${id}/domains`, { hostname }),
  removeDomain: (hostname: string) =>
    del<{ ok: true }>(`/api/platform/domains/${encodeURIComponent(hostname)}`),
  setPrimaryDomain: (hostname: string) =>
    patch<{ ok: true }>(`/api/platform/domains/${encodeURIComponent(hostname)}/primary`, {}),

  listSecrets: (id: string) =>
    get<{ secrets: PlatformSecret[]; status: TenantServicesStatus; secretsConfigured: boolean }>(
      `/api/platform/organizations/${id}/secrets`
    ),
  setSecret: (id: string, name: string, value: string) =>
    put<{ ok: true }>(`/api/platform/organizations/${id}/secrets/${name}`, { value }),
  deleteSecret: (id: string, name: string) =>
    del<{ ok: true }>(`/api/platform/organizations/${id}/secrets/${name}`),
  /** Une configuration SMTP ne se vérifie qu'en envoyant réellement. */
  sendTestEmail: (id: string, to: string) =>
    post<{ sent: boolean; from: string }>(`/api/platform/organizations/${id}/test-email`, { to }),

  // Comptes de l'équipe
  listAccounts: (id: string) =>
    get<{ accounts: PlatformAccount[] }>(`/api/platform/organizations/${id}/accounts`),
  createAccount: (id: string, payload: { email: string; displayName?: string }) =>
    post<{ email: string; temporaryPassword: string }>(
      `/api/platform/organizations/${id}/accounts`,
      payload
    ),
  resetAccountPassword: (id: string, userId: string) =>
    post<{ email: string; temporaryPassword: string }>(
      `/api/platform/organizations/${id}/accounts/${userId}/reset-password`
    ),
  setAccountActive: (id: string, userId: string, isActive: boolean) =>
    patch<{ ok: true }>(`/api/platform/organizations/${id}/accounts/${userId}`, { isActive }),
  setAccountPlatformAdmin: (id: string, userId: string, granted: boolean) =>
    post<{ ok: true }>(`/api/platform/organizations/${id}/accounts/${userId}/platform-admin`, {
      granted,
    }),

  // Suivi commercial
  listNotes: (id: string) => get<{ notes: PlatformNote[] }>(`/api/platform/organizations/${id}/notes`),
  addNote: (id: string, body: string, pinned = false) =>
    post<{ id: string }>(`/api/platform/organizations/${id}/notes`, { body, pinned }),
  pinNote: (id: string, noteId: string, pinned: boolean) =>
    patch<{ ok: true }>(`/api/platform/organizations/${id}/notes/${noteId}`, { pinned }),
  deleteNote: (id: string, noteId: string) =>
    del<{ ok: true }>(`/api/platform/organizations/${id}/notes/${noteId}`),

  listInteractions: (id: string) =>
    get<{ interactions: PlatformInteraction[] }>(`/api/platform/organizations/${id}/interactions`),
  addInteraction: (
    id: string,
    payload: {
      kind: InteractionKind;
      subject: string;
      body?: string;
      occurredAt?: string;
      nextActionAt?: string | null;
    }
  ) =>
    post<{ interaction: PlatformInteraction }>(
      `/api/platform/organizations/${id}/interactions`,
      payload
    ),
  updateInteraction: (id: string, interactionId: string, nextActionAt: string | null) =>
    patch<{ interaction: PlatformInteraction }>(
      `/api/platform/organizations/${id}/interactions/${interactionId}`,
      { nextActionAt }
    ),
  deleteInteraction: (id: string, interactionId: string) =>
    del<{ ok: true }>(`/api/platform/organizations/${id}/interactions/${interactionId}`),

  // Conservation des données
  setRetention: (id: string, entity: string, months: number) =>
    patch<{ ok: true }>(`/api/platform/organizations/${id}/retention/${entity}`, { months }),

  // Facturation
  listInvoices: (params: { status?: InvoiceStatus; organizationId?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.organizationId) search.set('organizationId', params.organizationId);
    return get<{ invoices: PlatformInvoice[]; totals: BillingTotals }>(
      `/api/platform/invoices?${search}`
    );
  },
  invoice: (id: string) =>
    get<{ invoice: PlatformInvoice; lines: InvoiceLine[] }>(`/api/platform/invoices/${id}`),
  createInvoice: (payload: {
    organizationId: string;
    lines: InvoiceLine[];
    vatRate?: number;
    periodStart?: string;
    periodEnd?: string;
    notes?: string;
  }) => post<{ id: string }>('/api/platform/invoices', payload),
  updateInvoice: (
    id: string,
    payload: { lines?: InvoiceLine[]; vatRate?: number; notes?: string }
  ) => patch<{ ok: true }>(`/api/platform/invoices/${id}`, payload),
  deleteInvoice: (id: string) => del<{ ok: true }>(`/api/platform/invoices/${id}`),
  /** Idempotente : une agence n'est facturée qu'une fois par période. */
  generateInvoices: (periodStart?: string) =>
    post<{ periodStart: string; created: string[]; skipped: string[] }>(
      '/api/platform/invoices/generate',
      { periodStart }
    ),
  issueInvoice: (id: string, dueDays = 30) =>
    post<{ ok: true; number: string }>(`/api/platform/invoices/${id}/issue`, { dueDays }),
  markInvoicePaid: (id: string, payload: { paidOn?: string; note?: string } = {}) =>
    post<{ ok: true }>(`/api/platform/invoices/${id}/paid`, payload),
  creditInvoice: (id: string, reason: string) =>
    post<{ id: string; number: string }>(`/api/platform/invoices/${id}/credit-note`, { reason }),
  sendInvoice: (id: string, reminder = false) =>
    post<{ sent: boolean; to: string }>(`/api/platform/invoices/${id}/send`, { reminder }),

  // Zone de danger
  /** `files` compte les fichiers effacés du disque : ils ne sont pas en base. */
  purge: (id: string, category: string) =>
    post<{ deleted: number; files: number; label: string }>(
      `/api/platform/organizations/${id}/purge`,
      { category }
    ),
  deletionStatus: (id: string) =>
    get<{ deletable: boolean; blockers: DeletionBlocker[]; slug: string }>(
      `/api/platform/organizations/${id}/permanent`
    ),
  deletePermanently: (id: string, confirmSlug: string) =>
    request<{ ok: true }>(`/api/platform/organizations/${id}/permanent`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmSlug }),
    }),
};

/** Noms acceptés côté serveur. Liste fermée, tenue identique à `SECRET_NAMES`. */
export const PLATFORM_SECRETS = [
  { name: 'stripe_secret_key', label: 'Clé secrète Stripe', placeholder: 'sk_live_…' },
  { name: 'stripe_webhook_secret', label: 'Signature du webhook Stripe', placeholder: 'whsec_…' },
  { name: 'smtp_password', label: 'Mot de passe SMTP', placeholder: '••••••••' },
  {
    name: 'gemini_api_key',
    label: 'Clé API Gemini (factures, devis, relecture, traduction)',
    placeholder: 'AIza…',
  },
  { name: 'hcaptcha_secret', label: 'Clé secrète hCaptcha (formulaire public)', placeholder: 'ES_…' },
] as const;
